import { useMemo, useState } from 'react';

import { createColumnHelper, getCoreRowModel, getFilteredRowModel, useReactTable } from '@tanstack/react-table';

import {
  DeletionFailureReason,
  DeletionStatus,
  FileDeletionType,
} from '../../../../../../../../../../backend/src/types/types';
import { DELETION_FAILURE_CONFIG } from '../../utils';
import { TrackingEventsV2 } from '@/analytics/eventsV2';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { useGetDeletedFiles } from '@/api/queries/useGetDeletedFiles';
import formatDate from '@/helpers/formatDate';
import { downloadDeletionBatchReport } from '@/helpers/report-utils';
import { Icon } from '@/new/components/icon';
import { Badge } from '@/new/components/shadcn/badge';
import { Button } from '@/new/components/shadcn/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/new/components/shadcn/dialog';
import { ScrollableTable } from '@/new/components/table/scrollable-table';

interface FailureBannerProps {
  failureReason: string | null;
  completedDocs: number | null;
  totalDocs: number | null;
}

function FailureBanner({ failureReason, completedDocs, totalDocs }: FailureBannerProps) {
  // Free-form column at the DB level; fall back to Unknown copy if the backend ever sends
  // something the frontend doesn't recognise.
  const knownReason =
    failureReason !== null && failureReason in DELETION_FAILURE_CONFIG
      ? (failureReason as DeletionFailureReason)
      : DeletionFailureReason.Unknown;
  const config = DELETION_FAILURE_CONFIG[knownReason];

  return (
    <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">
      <div className="mb-1 flex items-center gap-2 font-medium">
        <Icon className="lucide--triangle-alert h-4 w-4" />
        {config.label}
      </div>
      <p className="mb-1 text-xs">{config.description}</p>
      {totalDocs !== null && (
        <p className="text-xs">
          Deleted {completedDocs ?? 0} of {totalDocs} documents before failure.
        </p>
      )}
      <p className="mt-1 text-xs italic">{config.resolution}</p>
    </div>
  );
}

interface FlattenedDeletion {
  id: string;
  filename: string;
  deletedAt: string;
  deletedBy: string;
  type: FileDeletionType;
}

const columnHelper = createColumnHelper<FlattenedDeletion>();

interface DeletionDetailModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  matterId: string;
  deletionId: string;
}

export function DeletionDetailModal({ open, onOpenChange, matterId, deletionId }: DeletionDetailModalProps) {
  const [globalFilter, setGlobalFilter] = useState('');
  const { data: deletionHistory, isLoading } = useGetDeletedFiles(matterId);

  const deletion = useMemo(() => {
    return deletionHistory?.find((d) => d.id === deletionId);
  }, [deletionHistory, deletionId]);

  const flattenedDeletions = useMemo(() => {
    if (!deletion) return [];

    return deletion.fileDeletion.map((file, index) => ({
      id: `${deletion.id}-${index}`,
      filename: file.filename,
      deletedAt: deletion.createdAt,
      deletedBy: deletion.createdBy?.email ?? 'Unknown',
      type: deletion.type,
    }));
  }, [deletion]);

  const fileCount = deletion?.fileDeletion?.length ?? 0;

  const columns = useMemo(
    () => [
      columnHelper.accessor('filename', {
        header: () => <span>Filename</span>,
        cell: (info) => (
          <div className="flex items-center gap-2">
            <Icon className="lucide--file h-3.5 w-3.5 text-gray-400" />
            <span className="text-sm text-gray-800">{info.getValue()}</span>
          </div>
        ),
      }),
      columnHelper.accessor('type', {
        header: () => <span>Deletion Type</span>,
        cell: (info) => {
          const type = info.getValue();
          const isStaged = type === FileDeletionType.Staged;
          return (
            <Badge variant={isStaged ? 'secondary' : 'default'} className="text-xs">
              <Icon className={`${isStaged ? 'lucide--clock' : 'lucide--send'} mr-1 h-3 w-3`} />
              {isStaged ? 'Before Submission' : 'After Submission'}
            </Badge>
          );
        },
      }),
      columnHelper.accessor('deletedBy', {
        header: () => <span>Deleted By</span>,
        cell: (info) => <span className="text-sm text-gray-600">{info.getValue()}</span>,
      }),
      columnHelper.accessor('deletedAt', {
        header: () => <span>Date</span>,
        cell: (info) => <span className="text-sm text-gray-600">{formatDate(info.getValue(), true, true)}</span>,
      }),
    ],
    [],
  );

  const table = useReactTable<FlattenedDeletion>({
    data: flattenedDeletions,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    state: {
      globalFilter,
    },
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[80vh] max-w-6xl flex-col">
        <DialogHeader>
          <DialogTitle>Deletion Details</DialogTitle>
          <DialogDescription>
            {deletion && (
              <span className="flex items-center gap-2">
                <Icon className="lucide--calendar h-3.5 w-3.5" />
                <span>{formatDate(deletion.createdAt, true, true)}</span>
                {deletion.createdBy?.email && (
                  <>
                    <span className="text-gray-300">|</span>
                    <Icon className="lucide--user h-3.5 w-3.5" />
                    <span>{deletion.createdBy.email}</span>
                  </>
                )}
              </span>
            )}
          </DialogDescription>
        </DialogHeader>

        {deletion?.status === DeletionStatus.Failed && (
          <FailureBanner
            failureReason={deletion.failureReason}
            completedDocs={deletion.completedDocs}
            totalDocs={deletion.totalDocs}
          />
        )}

        <div className="min-h-0 flex-1 overflow-hidden">
          {fileCount > 0 ? (
            <ScrollableTable
              table={table}
              globalFilter={globalFilter}
              setGlobalFilter={setGlobalFilter}
              plural="deleted files"
              loading={isLoading}
              className="h-full max-h-[50vh] px-1 pt-1"
              additionalButtons={
                <Button
                  variant="default"
                  className="ml-auto"
                  onClick={() => {
                    trackCustomEvent(TrackingEventsV2.FileHistoryDownloadDeletionBatchReport, { matterId });
                    deletion && downloadDeletionBatchReport(deletion, deletionId);
                  }}
                  disabled={fileCount === 0}
                >
                  <Icon className="lucide--download h-3.5 w-3.5" />
                  Download Report
                </Button>
              }
            />
          ) : (
            <div className="py-8 text-center text-sm text-gray-500">
              {isLoading ? (
                <Icon className="lucide--loader-2 h-6 w-6 animate-spin text-gray-400" />
              ) : (
                'No files in this deletion.'
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
